import { type ElementType } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import {
  type ButtonOrLinkComponentProps,
  type LinkOrButton,
  getClickableElementStyles,
} from '../../styles/clickable-element/clickable-element';
import type { BoxProps } from '../box/box';
import { Box } from '../box/box';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';
import type { TextProps } from '../text/text';
import { Text } from '../text/text';

export type FileProps = {
  filePath: string;
  newFileName?: string;
};

export type FileDownloadProps = Pick<TextProps, 'children' | 'className'> &
  FileProps &
  ButtonOrLinkComponentProps & {
    'data-testid'?: string;
  } & Pick<BoxProps, 'ref'> & {
    target?: '_blank' | '_self';
  };

const TEST_IDS = {
  LINK: 'link-component',
  LEFT_ICON: 'left-icon',
  RIGHT_ICON: 'right-icon',
};

const isValidIconButton = (
  variant: ButtonOrLinkComponentProps['variant'],
  Icon: ElementType | undefined,
): Icon is ElementType => variant === 'icon';

export const FileDownload = ({
  children,
  disabled = false,
  size: rawSize,
  LeftIcon,
  RightIcon,
  Icon,
  iconColor,
  variant = LeftIcon || RightIcon || Icon ? 'text-link' : 'text-underline',
  className,
  filePath,
  newFileName = '',
  ref,
  target = '_self',
  ...props
}: FileDownloadProps) => {
  // we need to set the default size to inherit for text links
  const size = rawSize
    ? rawSize
    : variant === 'text-link' || variant === 'text-underline'
      ? 'inherit'
      : 'medium';

  const { styles, iconSize, textVariant, padding, spacing, resetStyles } =
    getClickableElementStyles({
      ...({ size, variant } as LinkOrButton),
      disabled,
      hasLeftIcon: !!LeftIcon,
      hasRightIcon: !!RightIcon,
    });

  return (
    <Box
      disabled={disabled}
      Component={disabled ? 'span' : 'a'}
      variant="initial" // css display property set by getClickableElementStyles
      rounded
      padding={padding}
      data-testid={TEST_IDS.LINK}
      ref={ref}
      spacing={spacing}
      href={filePath}
      download={newFileName ? newFileName : true}
      {...props}
      className={classFilter([resetStyles, styles, className])}
      target={target}
    >
      {LeftIcon ? (
        <LeftIcon size={iconSize} data-testid={TEST_IDS.LEFT_ICON} />
      ) : null}

      {isValidIconButton(variant, Icon) ? (
        <>
          <ScreenReaderText>{children}</ScreenReaderText>
          <Icon size={iconSize} color={iconColor} />
        </>
      ) : (
        <Text color="currentColor" variant={textVariant}>
          {children}
        </Text>
      )}

      {RightIcon ? (
        <RightIcon size={iconSize} data-testid={TEST_IDS.RIGHT_ICON} />
      ) : null}
    </Box>
  );
};
